/**
 * 商业化相关接口
 */

import HBMessage from "@/components/HBMessage";
import request from "@/utils/request";
import requestGd from "@/utils/requestGaoding";
import { omit } from "lodash";

/**
 * 获取用户权益列表
 * https://yapi.gaoding.com/project/1717/interface/api/123716
 * @param {{
 *  page_size?: number;
 *  page_num?: number;
 *  status?: number; //
 *  not_expired?: number;
 * }} query  用户权益状态 1：正常，2：已冻结，3：已回收，多个用逗号分隔 not_expired： 1 为不返回过期权益
 * @returns {Promise<{
 *  list: IUserRight[]
 * }>}
 */
export function getBizUserRightList({
  page_size = 20,
  page_num = 1,
  ...rest
} = {}) {
  return request({
    url: "/biz/user-rights",
    query: { page_size, page_num, ...rest },
  });
}

/**
 * 获取订单列表
 * https://yapi.gaoding.com/project/1717/interface/api/123725
 * @param {{
 *   status?: number | number[];
 *   user_id?: number;
 *   order_no?: number | number[];
 *   created_at_max?: string;
 *   created_at_min?: string;
 *   type?: number;
 *   device_id?: string;
 *   business_id?: string;
 *   invoice_id?: number;
 *   subject_type?: number;
 *   subject_id?: string;
 *   channel_id?: number | number[];
 *   gateway_creation_id?: string;
 *   charge_no?: string;
 *   page_num?: number;
 *   page_size?: number;
 * }} query
 * @param {{
 *  ignoreError?: boolean,
 *  ignoreMessage?: boolean,
 *  ignoreGdPermission?: boolean,
 * }} option
 * @returns {Promise<{msg: string, err: number}|*|undefined>}
 */
export function getBizOrderList(
  query,
  { ignoreError, ignoreGdPermission, ignoreMessage } = {}
) {
  const newQuery = Object.entries(
    omit(query ?? {}, "created_at_min", "created_at_max")
  ).reduce((r, [key, val]) => {
    r[key] = [null, undefined].includes(val)
      ? undefined
      : Array.isArray(val)
      ? val.join(",")
      : val;
    return r;
  }, {});

  if (query.created_at_max) {
    newQuery["created_at[lte]"] = query.created_at_max;
  }

  if (query.created_at_min) {
    newQuery["created_at[gte]"] = query.created_at_min;
  }

  return request({
    url: "/biz/orders",
    query: newQuery,
    ignoreError,
    ignoreGdPermission,
    ignoreMessage,
  });
}

/**
 * 获取发票列表
 * https://yapi.gaoding.com/project/1717/interface/api/123740
 * @param {{
 *   user_id: number;
 *   subject_type?: number;
 *   subject_id?: number;
 * }} query
 * @returns {Promise<*|{msg: string, err: number}|undefined>}
 */
export function getBizInvoiceList(query) {
  const newQuery = Object.entries(query ?? {}).reduce((r, [key, val]) => {
    r[key] = [null, undefined].includes(val)
      ? undefined
      : Array.isArray(val)
      ? val.join(",")
      : val;
    return r;
  }, {});

  return request({
    url: "/biz/invoices",
    query: newQuery,
  });
}

/**
 * 取消订单
 * https://yapi.gaoding.com/project/1717/interface/api/126086
 * @param {{order_no: string}} order_no
 */
export function cancelBizOrder({ order_no }) {
  return request({
    method: "post",
    url: `/biz/orders/${order_no}/cancel`,
  });
}

/**
 * 取消未支付订单
 * @returns
 */
export async function cancelUnPayOrder() {
  const { list, err } = await getBizOrderList(
    {
      status: 0,
    },
    true
  );

  if (err) return;
  // 过滤订阅订单
  const cancelList = list.filter((order) => {
    return !order.skus.find((sku) => sku?.ref_body?.goods?.is_auto_renew);
  });

  if (cancelList.length) {
    await cancelBizOrder({ order_no: cancelList[0].order_no });
  }
}

/**
 * 创建发票
 * https://yapi.gaoding.com/project/1717/interface/api/123748
 * @param data 发票数据
 * @returns {Promise<any|{msg: string, err: number}|undefined>}
 */
export function createBizInvoice(data) {
  return request({
    method: "post",
    url: "/biz/invoices",
    data,
  });
}

/**
 * 获取发票详情
 * https://yapi.gaoding.com/project/1717/interface/api/123747
 * @param invoice_id
 * @returns {Promise<any|{msg: string, err: number}|undefined>}
 */
export function getBizInvoice({ invoice_id }) {
  return request({
    url: `/biz/invoices/${invoice_id}`,
  });
}

/**
 * 获取下载列表
 * created_at_start 单位秒
 * @param {{
 *  type?: number;
 *  material_type?: number;
 *  keyword?: string;
 *  page_size?: number;
 *  page_num?: number;
 *  created_at_start?: number;
 *  created_at_end?: number;
 *  material_id?: number;
 *  vip_cert_subject_ids?: string;
 * }} query
 * @param {{ignoreGdPermission?: boolean, ignoreMessage?: boolean}=} option
 * @returns {Promise<{
 *  list: DownloadHistory[];
 * }>}
 */
export function getBizDownloadList(
  query = {},
  { ignoreGdPermission, ignoreMessage } = {}
) {
  return requestGd({
    url: "/trade/material/downloads",
    query,
    ignoreGdPermission,
    ignoreMessage,
  }).then((res) => {
    if (res.err) {
      return { list: [], size: 0, num: 0, total: 0 };
    }

    return {
      list: res.data,
      ...res.pagination,
    };
  });
}

/**
 * 获取下载详情
 * https://yapi.gaoding.com/project/1717/interface/api/121982
 * @param id
 * @returns {Promise<*|{msg: string, err: number}|undefined>}
 */
export function getBizDownloadDetail({ id }) {
  return request({
    url: `/biz/user-download/${id}`,
    ignoreMessage: true,
  }).then((res) => {
    if (res.err && res.msg === "no file_material") {
      HBMessage.error("请前往稿定设计操作");
    }
    return res;
  });
}

/**
 * 预创建发票
 * https://yapi.gaoding.com/project/1717/interface/api/125734
 * @param targets
 * @returns {Promise<*|{msg: string, err: number}|undefined>}
 */
export function preCreateBizInvoice({ targets, ...rest }) {
  return request({
    method: "post",
    url: "/biz/invoices/check",
    data: { targets },
    ...rest,
  });
}

/**
 * 获取相似素材列表
 * @param {Object} params
 * @param {string|number} params.pin_id - 采集ID
 * @param {number} [params.limit=20] - 每页数量
 * @param {number} params.page - 页码
 * @param {Array} [params.filter_ids] - 筛选ID列表
 * @param {string} [params.source_context_file_id_str] - 前置连续跳转内容 file_id 列表（实时上下文特征）
 * @param {string} [params.search_word] - 当前搜索词（实时上下文特征）
 * @returns {Promise}
 */
export function getSimiliarMaterial({
  pin_id,
  limit = 20,
  page,
  filter_ids = null,
  source_context_file_id_str,
  search_word,
}) {
  return request({
    url: `/pins/${pin_id}/materials/recommendweb`,
    query: {
      limit,
      page,
      fields: "pins:PIN",
      ...(filter_ids ? { filter_ids } : null),
      ...(source_context_file_id_str?.length > 0
        ? { source_context_file_id_str }
        : null),
      ...(search_word ? { search_word } : null),
    },
    ignoreError: true,
  });
}
/**
 * https://yapi.gaoding.com/project/1717/interface/api/146361
 * @param {
 * ids
 * page_num
 * page_size
 * sort
 * source
 * status} query
 * @returns
 */
export function getCounpons(query = {}) {
  return request({
    url: "/biz/users/coupons",
    query,
  });
}
/**
 * https://yapi.gaoding.com/project/1717/interface/api/146340
 * @param {
 * ids
 * source
 * status} query
 * @param {{
 *  ignoreGdPermission?: boolean,
 *  ignoreMessage?: boolean,
 *  ignoreError?: boolean
 * }} option
 * @returns {Promise<{count: 1}>}}
 */
export async function getCounponNum(
  query = {},
  { ignoreGdPermission, ignoreMessage, ignoreError } = {}
) {
  return request({
    url: "/biz/users/coupons/count",
    query,
    ignoreGdPermission,
    ignoreMessage,
    ignoreError,
  });
}

/**
 * 根据活动获取优惠券
 * https://yapi.gaoding.com/project/1717/interface/api/146340
 * @param {{
 * activity_code: string,
 * dm_msg?: string
 * }} data
 * @returns {Promise<{is_success: boolean, message: string, coupon_id: number, coupon}[] & {msg: string, err: number}>}}
 */
export async function getCounponByActivity(data = {}, ignoreError = false) {
  return request({
    method: "post",
    url: "/biz/users/send_coupon_dm",
    data,
    ignoreError,
  });
}

export function getGoods(query = {}) {
  return request({
    url: "/biz/resources/goods",
    query: {
      page_num: 1,
      page_size: 200,
      ...query,
    },
  });
}

/**
 *
 * @param {string[]} activity_codes
 * @returns {Promise<Record<string, {
 *    check: IActivityCheck
 *    activity_plus_buys: IActivityPlusBuy[]
 *  }>>
 * }
 */
export function getPlusActivity(activity_codes) {
  return request({
    url: `/biz/check_activity_resources_plus_buys`,
    method: "post",
    data: {
      activity_codes,
    },
  });
}
